import { useState } from "react";
import { CiSearch } from "react-icons/ci";

import { useLocalStorage } from "usehooks-ts";
import { isAddressEqual } from "viem";

import { CurrencyLabel } from "entities/currency";
import { Currency } from "shared/config";
import { Dialog } from "shared/ui/dialog";

import { Option } from "./option";

type Props<T> = {
  isOpen: boolean;
  selectedSymbol: string | undefined;
  options: T[];
  onSelect: (option: T) => void;
  onIsOpenChange: (isOpen: boolean) => void;
};

export const SelectCurrencyDialog = <T extends Currency>(props: Props<T>) => {
  const {
    options: optionsProp,
    isOpen,
    selectedSymbol,
    onIsOpenChange,
    onSelect,
  } = props;

  const [search, setSearch] = useState("");
  const options = optionsProp.filter((option) => {
    return option.symbol.toLowerCase().includes(search.toLowerCase().trim());
  });

  const [latestOptions, setLatestOptions] = useLocalStorage(
    "latest-options",
    options.slice(0, 3)
  );

  const handleSelect = (option: T) => {
    const isIncluded = latestOptions.some((o) =>
      isAddressEqual(o.address, option.address)
    );

    if (!isIncluded) {
      const newLatestOptions = [...latestOptions.slice(1), option];
      setLatestOptions(newLatestOptions);
    }

    onSelect(option);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onIsOpenChange}>
      <Dialog.Content className="gap-0">
        <Dialog.Body>
          <Dialog.Title showClose={true}>Search token</Dialog.Title>
          <div className="relative mx-2.5 flex items-center text-white">
            <CiSearch className="text-nobel/50 absolute top-1/2 left-4 size-6 -translate-y-1/2" />
            <input
              autoFocus={false}
              className="placeholder:text-nobel/50 bg-onyx/70 w-full rounded-xl py-4 pl-12 font-normal outline-hidden"
              placeholder="Search tokens by name or address"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
            />
          </div>
          <div className="mx-2.5 mt-4 flex gap-x-1.5">
            {latestOptions.map((option) => {
              return (
                <CurrencyLabel
                  key={option.symbol}
                  {...option}
                  onClick={() => handleSelect(option)}
                />
              );
            })}
          </div>
          <ul className="mt-5 flex h-96 min-h-96 flex-col overflow-y-auto border-t border-t-white/5 pt-5">
            {options.map((option) => {
              return (
                <Option
                  onClick={() => {
                    handleSelect(option);
                  }}
                  key={`${option.symbol}`}
                  isSelected={selectedSymbol === option.symbol}
                  option={option}
                />
              );
            })}
          </ul>
        </Dialog.Body>
      </Dialog.Content>
    </Dialog>
  );
};
